// react
import * as React from 'react';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';

// api
import { useGetEnergyGroupsQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import EnergyGroupTypeIcon from '@/components/ui/icon/EnergyGroupTypeIcon';

// relative
import type { NavItemConfig } from '../navigationTypes';

const normalizeType = (type: string | null | undefined) => {
  const s = String(type ?? '').toLowerCase();
  if (s.includes('power')) return 'Power';
  if (s.includes('heat')) return 'Heat';
  if (s.includes('water')) return 'Water';
  return 'Other';
};

const TYPE_ORDER = ['Power', 'Heat', 'Water', 'Other'];

/**
 * Custom hook to dynamically build navigation items for energy groups
 *
 * Returns an array of NavItemConfig items based on the current energy groups.
 * Groups are filtered (excluding portfolio types), sorted by type and name,
 * and rendered with appropriate icons.
 *
 * @returns Array of NavItemConfig items for deepdive navigation
 *
 * @example
 * ```tsx
 * const energyGroupItems = useEnergyGroupNavItems();
 *
 * // Merge into existing config
 * const config = mergeNavChildren(NAV_CONFIG, 'energyxplorer.deepdive', energyGroupItems);
 * ```
 */
export const useEnergyGroupNavItems = (): NavItemConfig[] => {
  const { accountId } = useParams({ strict: false });

  const { data: energyGroups } = useGetEnergyGroupsQuery(
    { accountIds: accountId ? [Number(accountId)] : undefined },
    {
      skip: !accountId,
    },
  );

  return React.useMemo(() => {
    // Filter out portfolio groups
    const groups = (energyGroups ?? []).filter(
      (g) =>
        !String(g.type ?? '')
          .toLowerCase()
          .includes('portfolio'),
    );

    // Group by normalized type (Power, Heat, Water, Other)
    const byType = groups.reduce(
      (acc, g) => {
        const key = normalizeType(g.type);
        acc[key] = acc[key] || [];
        acc[key].push(g);
        return acc;
      },
      {} as Record<string, typeof groups>,
    );

    let children: NavItemConfig[] = [];

    // Build nav items in type order
    TYPE_ORDER.forEach((type) => {
      const list = byType[type];
      if (!list?.length) return;

      list
        .slice()
        .sort((a, b) =>
          String(a.name ?? '').localeCompare(String(b.name ?? '')),
        )
        .forEach((g) => {
          const path =
            (g.energyTreeNodes?.length ?? 0) === 0
              ? `/$accountId/energyxplorer/deepdive/${g.energyGroupId}/create-first-node`
              : `/$accountId/energyxplorer/deepdive/${g.energyGroupId}`;

          children.push({
            id: `energy-group-${g.energyGroupId}`,
            labelKey: g.name ?? 'Unnamed group', // Using labelKey but with actual label
            iconName: type.toLowerCase(), // Will be replaced by actual icon below
            path: path,
            // Store the actual icon as metadata (will be handled during hydration)
            _icon: <EnergyGroupTypeIcon type={type} />,
            visible: (ctx) =>
              Boolean(
                ctx.adminViewMode || ctx.services?.includes('EnergyXplorer'),
              ),
          } as NavItemConfig & { _icon?: React.ReactNode });
        });
    });

    // Add "Create DeepDive" action item
    children.push({
      id: 'deepdive-manage',
      labelKey: 'Opret DeepDive',
      iconName: 'add',
      path: '/$accountId/energyxplorer/deepdive/create',
      _icon: <AddRoundedIcon />,
      visible: (ctx) =>
        Boolean(ctx.adminViewMode || ctx.services?.includes('EnergyXplorer')),
    } as NavItemConfig & { _icon?: React.ReactNode });

    children = [
      // {
      //   id: 'deepdive-dashboard',
      //   labelKey: 'navigation.items.dashboard',
      //   iconName: 'dashboard',
      //   path: '/$accountId/energyxplorer/deepdive/dashboard',
      // } as NavItemConfig & { _icon?: React.ReactNode },
      ...children,
    ];

    return children;
  }, [energyGroups]);
};
